import type { DynamicValue } from "@skilitics/nautilus-core";
import type { DesignerNodeData, ValueReferenceEdge } from "@skilitics/nautilus-core-designer-data";
import { isReferencedValue, referencedNodeValue, traverseNodeSubtree } from "@skilitics/nautilus-core-designer-data";

import { buildRuntimeBindings } from "../../publish/buildRuntimeBindings.ts";
import type { BuildDesignerNode } from "../../state/buildDesignerNode.ts";
import type { GetParentContextById } from "../../state/index.ts";
import type { GetManifestByTypeId } from "../../types/index.ts";
import type { GetNodeValue, ResolveAvailableValues } from "../querying/index.ts";
import type { ResolveDesignTimeValue } from "../resolveDesignTimeValue.ts";
import type { GetValueSubtypeDefinition } from "../subtype-definitions/index.ts";
import { parentFoldsChildResult } from "./childResultEdges.ts";

interface Deps {
  getManifest: GetManifestByTypeId;
  buildDesignerNode: BuildDesignerNode;
  getParentContextById: GetParentContextById;
  getNodeValue: GetNodeValue;
  resolveAvailableValues: ResolveAvailableValues;
  resolveDesignTimeValue: ResolveDesignTimeValue;
  getValueSubtypeDefinition: GetValueSubtypeDefinition;
}

/**
 * Collect every node-value dependency edge in a design subtree:
 *
 * - node-value ValueConfigs stored in node configs (design-time value chains)
 * - `__dv` node-property refs produced by running each manifest's real
 *   `getRuntimeBindings` build — exactly the edges publish will emit, with no
 *   heuristics over `nodeReferences`
 * - child-result edges for slots whose result is folded into the parent's, so a loop
 *   that only closes through the hierarchy (option → hiders → predicate → other
 *   option → …) is visible without running the design
 *
 * Feed the result to `findValueReferenceCycles`.
 */
export const collectValueReferenceEdgesCreate =
  (deps: Deps) =>
  (rootNode: DesignerNodeData): ValueReferenceEdge[] => {
    const edges: ValueReferenceEdge[] = [];
    traverseNodeSubtree(rootNode, (node, ancestors, slotName) => {
      collectConfigValueEdges(node, edges);
      collectRuntimeBindingEdges(node, ancestors, deps, edges);
      collectChildResultEdge(node, ancestors[0], slotName, deps, edges);
    });
    return edges;
  };

export type CollectValueReferenceEdges = ReturnType<typeof collectValueReferenceEdgesCreate>;

/**
 * Deep-walk an arbitrary config/bindings tree, reporting each leaf `recognise`
 * claims along with its dotted path. Recursion stops at a claimed leaf.
 */
const forEachRecognisedLeaf = <TLeaf>(
  root: unknown,
  recognise: (value: object) => TLeaf | undefined,
  onLeaf: (leaf: TLeaf, path: string) => void,
) => {
  const walk = (value: unknown, path: string) => {
    if (Array.isArray(value)) {
      for (const [index, item] of value.entries()) walk(item, `${path}[${index}]`);
      return;
    }
    if (typeof value !== "object" || value === null) return;
    const leaf = recognise(value);
    if (leaf !== undefined) {
      onLeaf(leaf, path);
      return;
    }
    for (const [key, child] of Object.entries(value)) walk(child, path ? `${path}.${key}` : key);
  };
  walk(root, "");
};

const collectConfigValueEdges = (node: DesignerNodeData, edges: ValueReferenceEdge[]) => {
  forEachRecognisedLeaf(
    node.config,
    (value) => (isReferencedValue(value) ? value : undefined),
    (config, path) => {
      const nodeValue = referencedNodeValue(config);
      if (nodeValue) {
        edges.push({ fromNodeId: node.id, toNodeId: nodeValue.nodeId, name: path, property: nodeValue.name });
      }
    },
  );
};

const collectChildResultEdge = (
  node: DesignerNodeData,
  parent: DesignerNodeData | undefined,
  slotName: string | undefined,
  deps: Deps,
  edges: ValueReferenceEdge[],
) => {
  if (!parent || !slotName) return;
  const parentTerminus = deps.getManifest(parent.typeId, { ignoreIfNotFound: true })?.terminus;
  if (!parentFoldsChildResult(parentTerminus, slotName)) return;
  edges.push({ fromNodeId: parent.id, toNodeId: node.id, kind: "child-result", name: slotName });
};

const collectRuntimeBindingEdges = (
  node: DesignerNodeData,
  ancestors: readonly DesignerNodeData[],
  deps: Deps,
  edges: ValueReferenceEdge[],
) => {
  const { designer } = deps.getManifest(node.typeId, { ignoreIfNotFound: true }) ?? {};
  if (!designer?.getRuntimeBindings) return;

  let bindings: unknown;
  try {
    bindings = buildRuntimeBindings(deps.buildDesignerNode(node), designer, {
      // traverseNodeSubtree yields nearest-first; publish resolves ancestor specs root-first
      ancestors: [...ancestors].reverse(),
      parentContext: deps.getParentContextById(node.id),
      getNodeValue: deps.getNodeValue,
      resolveAvailableValues: deps.resolveAvailableValues,
      resolveDesignTimeValue: deps.resolveDesignTimeValue,
      getValueSubtypeDefinition: deps.getValueSubtypeDefinition,
    });
  } catch {
    // A mid-edit config may not build cleanly; publish rendering still surfaces that.
    return;
  }

  forEachRecognisedLeaf(
    bindings,
    (value) => (isDynamicValue(value) ? value.__dv : undefined),
    (dynamicValue, path) => {
      // Self-refs here are manifest fallback defaults (target unset resolves to the
      // node itself), not authored loops — the picker already blocks self-targets.
      if (dynamicValue.type === "np" && dynamicValue.nodeId !== node.id) {
        edges.push({ fromNodeId: node.id, toNodeId: dynamicValue.nodeId, name: path, property: dynamicValue.property });
      }
    },
  );
};

const isDynamicValue = (value: object): value is DynamicValue => "__dv" in value;
